import { useEffect, useRef, useState } from 'react';
import { CopySimple } from '@phosphor-icons/react';
import { css, cx } from '@eti/styles';
import { useSnowplowTracking, useTranslation } from '@eti/providers';
import type { TextVariantsType } from '@etg/wings';
import { Inline, Notice, Text, textVariants, vars } from '@etg/wings';
import { constants } from '@eti/utils';
import useClipboard from '../../../../common/custom-hooks/useClipboard';

const buttonStyles = css`
  background: unset;
  border: none;
  cursor: pointer;
  display: flex;
  padding: 2px;

  &:hover {
    background-color: #f6f6f6;
  }

  &:focus-visible {
    outline-color: ${vars.colors.focusRing};
  }
`;

const inlineStyles = (justifyContentVariant: string) => css`
  justify-content: ${justifyContentVariant};
  position: relative;
`;

const noticeStyles = (textWidth: number) => css`
  background-color: ${vars.colors.signal.positive.tint};
  border-radius: 3px;
  bottom: calc(100% + 10px);
  left: calc(${`${textWidth}px`} / 2);
  padding: 10px;
  position: absolute;
  transform: translateX(-50%);
  z-index: 999;

  ::after {
    border-color: ${vars.colors.signal.positive.tint} transparent transparent transparent;
    border-style: solid;
    border-width: 12px;
    content: '';
    left: 50%;
    position: absolute;
    top: 100%;
    transform: translateX(-50%);
  }
`;

const textStyles = css`
  word-break: break-all;
`;

export interface TextWithCopyButtonProps {
  'data-testid': string;
  justifyContentVariant?: 'space-between' | 'flex-start' | 'flex-end';
  shouldApplyMouseflowMask?: boolean;
  snowplowInfo?: {
    category: string;
    label: string;
  };
  text: string;
  textVariant?: TextVariantsType;
}
const TextWithCopyButton = ({
  'data-testid': dataTestId,
  justifyContentVariant = 'flex-start',
  shouldApplyMouseflowMask,
  snowplowInfo,
  text,
  textVariant,
}: TextWithCopyButtonProps) => {
  const { t } = useTranslation();
  const { isSnowplowTrackingEnabled, sendTrackEvent } = useSnowplowTracking();
  const [textWidth, setTextWidth] = useState<number>(0);
  const textRef = useRef<HTMLInputElement>(null);
  const [isCopied, copyToClipboard] = useClipboard();

  useEffect(() => {
    const textElement = textRef.current;
    if (textElement) {
      setTextWidth(textElement.getBoundingClientRect().width);
    }
  }, [textRef]);

  const handleOnButtonClick = () => {
    if (isSnowplowTrackingEnabled && snowplowInfo) {
      sendTrackEvent({
        category: snowplowInfo.category,
        action: 'Click',
        label: snowplowInfo.label,
      });
    }
    copyToClipboard(text);
  };

  return (
    <Inline
      alignY="center"
      className={inlineStyles(justifyContentVariant)}
      data-testid={dataTestId}
      noWrap
      spacing={4}
    >
      <Text
        className={cx(textStyles, { [constants.MOUSE_FLOW_CLASS_NAMES]: shouldApplyMouseflowMask })}
        ref={textRef}
        variant={textVariant}
      >
        {text}
      </Text>
      {isCopied && (
        <Notice
          aria-label={t('ScreenReader.CopyButton.Pressed.AriaLabel.Text', text)}
          className={noticeStyles(textWidth)}
          role="alert"
        >
          <Text aria-hidden variant={textVariants._14_BOLD}>
            {t('OrderDetails.Copy.Button.Text')}
          </Text>
        </Notice>
      )}
      <button
        aria-label={t('ScreenReader.CopyButton.AriaLabel.Text', text)}
        className={buttonStyles}
        onClick={handleOnButtonClick}
        type="button"
      >
        <CopySimple aria-hidden size={20} />
      </button>
    </Inline>
  );
};

export default TextWithCopyButton;
